import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Send,
  Plus,
  Trash2,
  Play,
  Download,
  Webhook,
  Users,
  Mail,
  Linkedin,
  Twitter,
  ChevronDown,
  ChevronRight,
  Edit,
  Clock,
  Check,
  Sparkles,
  ArrowRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EntitlementGate from "@/components/EntitlementGate";

const CHANNEL_ICONS = {
  email: Mail,
  linkedin: Linkedin,
  twitter: Twitter
};

export default function CampaignBuilder({ user }) {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newCampaignTitle, setNewCampaignTitle] = useState("");
  const [showNewForm, setShowNewForm] = useState(false);
  const [recipients, setRecipients] = useState([{ name: "", email: "", company: "", role: "" }]);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const res = await api.get("/outreach-pro/campaigns");
      setCampaigns(res.data.campaigns || []);
    } catch (e) {
      if (e.response?.status !== 402) {
        console.error("Error fetching campaigns:", e);
      }
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async () => {
    if (!newCampaignTitle.trim()) {
      toast.error("Please enter a campaign title");
      return;
    }
    
    setCreating(true);
    try {
      const res = await api.post("/outreach-pro/campaigns", {
        title: newCampaignTitle,
        tone: "professional"
      });
      setCampaigns([res.data.campaign, ...campaigns]);
      setSelectedCampaign(res.data.campaign);
      setNewCampaignTitle("");
      setShowNewForm(false);
      toast.success("Campaign created!");
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Failed to create campaign");
    } finally {
      setCreating(false);
    }
  };

  const addStep = async () => {
    if (!selectedCampaign) return;
    
    try {
      const stepOrder = (selectedCampaign.steps?.length || 0) + 1;
      const res = await api.post(`/outreach-pro/campaigns/${selectedCampaign.id}/steps`, {
        order: stepOrder,
        wait_days: stepOrder === 1 ? 0 : 3,
        channel: "email",
        ai_prompt: ""
      });
      
      setSelectedCampaign({
        ...selectedCampaign,
        steps: [...(selectedCampaign.steps || []), res.data.step]
      });
      toast.success("Step added!");
    } catch (e) {
      toast.error("Failed to add step");
    }
  };

  const updateStep = async (stepId, updates) => {
    if (!selectedCampaign) return;
    
    try {
      await api.put(`/outreach-pro/campaigns/${selectedCampaign.id}/steps/${stepId}`, updates);
      
      setSelectedCampaign({
        ...selectedCampaign,
        steps: selectedCampaign.steps.map(s => 
          s.id === stepId ? { ...s, ...updates } : s
        )
      });
    } catch (e) {
      toast.error("Failed to update step");
    }
  };

  const deleteStep = async (stepId) => {
    if (!selectedCampaign) return;
    
    try {
      await api.delete(`/outreach-pro/campaigns/${selectedCampaign.id}/steps/${stepId}`);
      setSelectedCampaign({
        ...selectedCampaign,
        steps: selectedCampaign.steps.filter(s => s.id !== stepId)
      });
      toast.success("Step deleted");
    } catch (e) {
      toast.error("Failed to delete step");
    }
  };

  const generateMessages = async () => {
    if (!selectedCampaign) return;
    
    const validRecipients = recipients.filter(r => r.name && r.email);
    if (validRecipients.length === 0) {
      toast.error("Please add at least one recipient with name and email");
      return;
    }
    
    if (!selectedCampaign.steps?.length) {
      toast.error("Please add at least one step to the campaign");
      return;
    }
    
    setGenerating(true);
    try {
      const res = await api.post(`/outreach-pro/campaigns/${selectedCampaign.id}/generate`, {
        recipients: validRecipients
      });
      toast.success(`Generated ${res.data.count} messages!`);
      
      // Refresh campaign to get updated stats
      const updated = await api.get(`/outreach-pro/campaigns/${selectedCampaign.id}`);
      setSelectedCampaign(updated.data);
      setRecipients([{ name: "", email: "", company: "", role: "" }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate messages");
    } finally {
      setGenerating(false);
    }
  };

  const exportCampaign = async (format = "csv") => {
    if (!selectedCampaign) return;
    
    try {
      const res = await api.post(`/outreach-pro/campaigns/${selectedCampaign.id}/export`);
      
      // Download CSV
      const blob = new Blob([res.data.csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${res.data.count} messages`);
    } catch (e) {
      toast.error("Failed to export");
    }
  };

  const addRecipient = () => {
    setRecipients([...recipients, { name: "", email: "", company: "", role: "" }]);
  };

  const updateRecipient = (index, field, value) => {
    const updated = [...recipients];
    updated[index][field] = value;
    setRecipients(updated);
  };

  const removeRecipient = (index) => {
    if (recipients.length > 1) {
      setRecipients(recipients.filter((_, i) => i !== index));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <EntitlementGate feature="outreach" user={user}>
      <div className="space-y-6" data-testid="campaign-builder">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-indigo-500 flex items-center gap-2">
              <Send className="w-6 h-6 text-indigo-400" />
              Cold Outreach Pro
            </h1>
            <p className="text-indigo-400 text-sm mt-1">
              Build multi-step outreach campaigns with AI-personalized messages
            </p>
          </div>
          
          <button
            onClick={() => setShowNewForm(true)}
            className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Campaign
          </button>
        </div>

        {/* New Campaign Form */}
        {showNewForm && (
          <Card className="bg-slate-800/50 border-indigo-500/30">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newCampaignTitle}
                  onChange={(e) => setNewCampaignTitle(e.target.value)}
                  placeholder="Campaign name (e.g., Q1 Agency Outreach)"
                  className="flex-1 px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                  onKeyDown={(e) => e.key === "Enter" && createCampaign()}
                />
                <button
                  onClick={createCampaign}
                  disabled={creating}
                  className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {creating ? "Creating..." : "Create"}
                </button>
                <button
                  onClick={() => setShowNewForm(false)}
                  className="px-4 py-2 text-indigo-400 hover:text-indigo-500 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Campaigns List */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-indigo-400 uppercase tracking-wide">
              Campaigns ({campaigns.length})
            </h3>
            
            {campaigns.length === 0 ? (
              <Card className="bg-slate-800/30 border-slate-700/50 border-dashed">
                <CardContent className="py-8 text-center">
                  <Send className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-indigo-400 text-sm">No campaigns yet</p>
                  <button
                    onClick={() => setShowNewForm(true)}
                    className="text-indigo-400 text-sm mt-2 hover:text-indigo-400"
                  >
                    Create your first campaign
                  </button>
                </CardContent>
              </Card>
            ) : (
              campaigns.map((campaign) => (
                <button
                  key={campaign.id}
                  onClick={() => setSelectedCampaign(campaign)}
                  className={`w-full text-left p-4 rounded-xl border transition-all ${
                    selectedCampaign?.id === campaign.id
                      ? "bg-indigo-500/10 border-indigo-500/50"
                      : "bg-slate-800/30 border-slate-700/50 hover:border-slate-600"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-indigo-500">{campaign.title}</span>
                    <Badge className={
                      campaign.status === "active" ? "bg-green-500/20 text-green-400" :
                      campaign.status === "completed" ? "bg-blue-500/20 text-indigo-400" :
                      "bg-slate-500/20 text-indigo-400"
                    }>
                      {campaign.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span>{campaign.messages_generated || 0} messages</span>
                    <span>{campaign.recipients_count || 0} recipients</span>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Campaign Editor */}
          <div className="lg:col-span-2 space-y-6">
            {selectedCampaign ? (
              <>
                {/* Campaign Header */}
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-indigo-500">{selectedCampaign.title}</CardTitle>
                        <CardDescription className="text-indigo-400">
                          {selectedCampaign.steps?.length || 0} steps • {selectedCampaign.tone} tone
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={exportCampaign}
                          className="p-2 text-indigo-400 hover:text-indigo-500 hover:bg-slate-400 rounded-lg transition-colors"
                          title="Export CSV"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                {/* Steps */}
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-indigo-500 text-lg">Sequence Steps</CardTitle>
                      <button
                        onClick={addStep}
                        className="text-sm text-indigo-400 hover:text-indigo-400 flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Step
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {selectedCampaign.steps?.length === 0 ? (
                      <div className="text-center py-6 text-slate-400">
                        <p>No steps yet. Add your first step to define the outreach sequence.</p>
                      </div>
                    ) : (
                      selectedCampaign.steps?.map((step, index) => {
                        const ChannelIcon = CHANNEL_ICONS[step.channel] || Mail;
                        return (
                          <div key={step.id} className="p-4 bg-slate-900/50 rounded-xl border border-slate-700">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400 font-medium">
                                  {index + 1}
                                </div>
                                <div className="flex items-center gap-2">
                                  <ChannelIcon className="w-4 h-4 text-indigo-400" />
                                  <Select
                                    value={step.channel}
                                    onValueChange={(v) => updateStep(step.id, { channel: v })}
                                  >
                                    <SelectTrigger className="w-28 h-8 bg-transparent border-slate-600 text-sm">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-slate-800 border-slate-700">
                                      <SelectItem value="email">Email</SelectItem>
                                      <SelectItem value="linkedin">LinkedIn</SelectItem>
                                      <SelectItem value="twitter">Twitter</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                {step.wait_days > 0 && (
                                  <Badge variant="outline" className="text-xs text-indigo-400">
                                    <Clock className="w-3 h-3 mr-1" />
                                    Wait {step.wait_days} days
                                  </Badge>
                                )}
                              </div>
                              <button
                                onClick={() => deleteStep(step.id)}
                                className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            <Textarea
                              value={step.ai_prompt || ""}
                              onChange={(e) => updateStep(step.id, { ai_prompt: e.target.value })}
                              placeholder="AI prompt: What should this message accomplish? (e.g., 'Introduce yourself and mention their recent work')"
                              className="min-h-[80px] bg-slate-800/50 border-slate-600 text-indigo-500 text-sm placeholder:text-slate-400"
                            />
                          </div>
                        );
                      })
                    )}
                  </CardContent>
                </Card>

                {/* Recipients */}
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-indigo-500 text-lg flex items-center gap-2">
                        <Users className="w-5 h-5 text-green-400" />
                        Recipients
                      </CardTitle>
                      <button
                        onClick={addRecipient}
                        className="text-sm text-indigo-400 hover:text-indigo-400 flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Recipient
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {recipients.map((recipient, index) => (
                      <div key={index} className="grid grid-cols-4 gap-2 items-center">
                        <input
                          type="text"
                          value={recipient.name}
                          onChange={(e) => updateRecipient(index, "name", e.target.value)}
                          placeholder="Name"
                          className="px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 text-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                        />
                        <input
                          type="email"
                          value={recipient.email}
                          onChange={(e) => updateRecipient(index, "email", e.target.value)}
                          placeholder="Email"
                          className="px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 text-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                        />
                        <input
                          type="text"
                          value={recipient.company}
                          onChange={(e) => updateRecipient(index, "company", e.target.value)}
                          placeholder="Company"
                          className="px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 text-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                        />
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={recipient.role}
                            onChange={(e) => updateRecipient(index, "role", e.target.value)}
                            placeholder="Role"
                            className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 text-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                          />
                          {recipients.length > 1 && (
                            <button
                              onClick={() => removeRecipient(index)}
                              className="p-2 text-slate-400 hover:text-red-400 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Generate Button */}
                    <button
                      onClick={generateMessages}
                      disabled={generating}
                      className="w-full mt-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {generating ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5" />
                          Generate Personalized Messages
                        </>
                      )}
                    </button>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="bg-slate-800/30 border-slate-700/50 border-dashed">
                <CardContent className="py-16 text-center">
                  <ArrowRight className="w-10 h-10 text-slate-600 mx-auto mb-3 -rotate-180" />
                  <p className="text-indigo-400">Select a campaign to edit</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </EntitlementGate>
  );
}
